import { defineStore, storeToRefs } from 'pinia'
import { ref, Ref, watch, computed } from 'vue'
import { useAuthUserStore } from 'stores/auth-user-store'
import { socket } from '../services/socket'
import * as crypto from 'crypto-js'
import { Platform } from 'quasar'
import { DisconnectDescription, Socket } from 'socket.io-client/build/esm/socket'

export enum WebSocketAuthStatus {
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  AUTHENTICATED = 'AUTHENTICATED'
}

export enum WebSocketStatus {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN'
}

type LastMessage = { timestamp: number | null, message: string | null }

export type UnsubscribeCallback = () => void;

export interface SocketAcknowledgeCallback { (error: unknown, data: unknown): void }
export interface SocketMessageListener { (data: unknown, error: unknown): void }

enum WebSocketClientMessage {
  SUBSCRIBE_LIST = 'subscribe list',
  SUBSCRIBE_WATCHLIST = 'subscribe watchlist',
  SUBSCRIBE_TITLE_MESSAGES = 'subscribe title messages',
  SUBSCRIBE_TITLE = 'subscribe title',
  SUBSCRIBE_TITLE_TICKS = 'subscribe title ticks',
  UNSUBSCRIBE_TITLE = 'unsubscribe title',
  SUBSCRIBE_STD_SIGNALS = 'subscribe std signals',
  SUBSCRIBE_SIGNALS = 'subscribe signals',
  AUTHORIZE = 'authorize'
}
enum WebSocketEvent {
  MESSAGE = 'message',
  STD_SIGNAL = 'std_signal',
  SIGNAL = 'signal',
  BUZZ = 'buzz',
  TICK = 'tick'
}

export const webSocketStore = defineStore('webSocket',
  () => {
    const authUserStore = useAuthUserStore()
    const { isLoggedIn } = storeToRefs(authUserStore)

    const lastData : Ref<LastMessage> = ref({ timestamp: null, message: null })

    const socketStatus : Ref<WebSocketStatus> = ref(WebSocketStatus.CLOSED)
    const authStatus : Ref<WebSocketAuthStatus> = ref(WebSocketAuthStatus.UNAUTHENTICATED)

    const registeredListeners: SocketMessageListener[] = []

    const isSocketReady = computed(() => {
      return socketStatus.value === WebSocketStatus.OPEN && authStatus.value === WebSocketAuthStatus.AUTHENTICATED
    })

    // TODO: example binds the events via boot file.
    // As long as the websocket store does not leave the scope, binding here seems to work

    socket.on('connect', onConnected)
    socket.on('connect_error', onConnectError)
    socket.on('disconnect', onDisconnected)

    socket.onAny(onAnyEvent)

    function onDisconnected (reason: Socket.DisconnectReason, description?: DisconnectDescription) {
      console.log('onDisconnected', reason, description)
      authStatus.value = WebSocketAuthStatus.UNAUTHENTICATED
      socketStatus.value = WebSocketStatus.CLOSED
    }

    function onConnectError (err: Error) {
      console.log('onConnectError', err)
      authStatus.value = WebSocketAuthStatus.UNAUTHENTICATED
      socketStatus.value = WebSocketStatus.CLOSED
    }

    function onAnyEvent (eventName: string) {
      lastData.value.timestamp = Date.now()
      lastData.value.message = eventName
    }

    const fingerprint =
      process.env.WEBSOCKET_FINGERPRINT || crypto.MD5(JSON.stringify(Platform.is)).toString(crypto.enc.Hex)

    function onConnected () {
      socketStatus.value = WebSocketStatus.OPEN
      // TODO_NEXT: remove plain text password usage in websocket, avoid caching plain text credentials in local storage
      socket.emit(WebSocketClientMessage.AUTHORIZE, {
        username: authUserStore.getCredentials?.username,
        password: authUserStore.getCredentials?.password,
        fingerprint
      }, (err: unknown, data: unknown) => {
        if (err || !data) {
          console.log('authorize failed', err, data)
          authStatus.value = WebSocketAuthStatus.UNAUTHENTICATED
          socket.close()
        } else if (data) {
          authStatus.value = WebSocketAuthStatus.AUTHENTICATED
        }
      })
    }

    function emitSubscribeWatchlist (watchlistId: number, callback: SocketAcknowledgeCallback) {
      socket.emit(WebSocketClientMessage.SUBSCRIBE_WATCHLIST, watchlistId, callback)
    }

    function emitSubscribeList (listId: number, callback: SocketAcknowledgeCallback) {
      socket.emit(WebSocketClientMessage.SUBSCRIBE_LIST, listId, callback)
    }

    function emitSubscribeTitle (titleId: number|string,
      listener: SocketMessageListener,
      callback?: SocketAcknowledgeCallback) : UnsubscribeCallback {
      if (!registeredListeners.includes(listener)) {
        registeredListeners.push(listener)
        socket.on(WebSocketEvent.BUZZ, listener)
      }

      socket.emit(WebSocketClientMessage.SUBSCRIBE_TITLE, titleId, callback)

      return () => socket.off(WebSocketClientMessage.SUBSCRIBE_TITLE, listener)
    }

    function emitSubscribeTitles (
      titleIds: number[],
      listener: SocketMessageListener,
      callback?: SocketAcknowledgeCallback) : UnsubscribeCallback {
      return emitSubscribeTitle(titleIds.join(','), listener, callback)
    }

    function emitUnsubscribeTitles (titleIds: number[]) : void {
      socket.emit(WebSocketClientMessage.UNSUBSCRIBE_TITLE, titleIds.join(','))
    }

    function emitSubscribeStdSignals (listener: SocketMessageListener) {
      socket.on(WebSocketEvent.STD_SIGNAL, listener)
      socket.emit(WebSocketClientMessage.SUBSCRIBE_STD_SIGNALS)
    }

    function emitSubscribeSignals (listener: SocketMessageListener) {
      socket.on(WebSocketEvent.SIGNAL, listener)
      socket.emit(WebSocketClientMessage.SUBSCRIBE_SIGNALS)
    }

    function emitSubscribeTitleMessages (
      titleId: number|string,
      listener: SocketMessageListener) : UnsubscribeCallback {
      if (!registeredListeners.includes(listener)) {
        registeredListeners.push(listener)
        socket.on(WebSocketEvent.MESSAGE, listener)
      }

      socket.emit(WebSocketClientMessage.SUBSCRIBE_TITLE_MESSAGES, titleId)

      return () => socket.off(WebSocketClientMessage.SUBSCRIBE_TITLE_MESSAGES, listener)
    }

    function emitSubscribeTitlesMessages (titleIds: number[], listener: SocketMessageListener) : UnsubscribeCallback {
      return emitSubscribeTitleMessages(titleIds.join(','), listener)
    }

    function emitSubscribeTitlesTicks (
      titleIds: number[],
      listener: SocketMessageListener,
      callback?: SocketAcknowledgeCallback
    ) : UnsubscribeCallback {
      return emitSubscribeTitleTicks(titleIds.join(','), listener, callback)
    }

    function emitSubscribeTitleTicks (
      titleId: number|string,
      listener: SocketMessageListener,
      callback?: SocketAcknowledgeCallback
    ) : UnsubscribeCallback {
      if (!registeredListeners.includes(listener)) {
        registeredListeners.push(listener)
        socket.on(WebSocketEvent.TICK, listener)
      }

      socket.emit(WebSocketClientMessage.SUBSCRIBE_TITLE_TICKS, titleId, callback)

      return () => socket.off(WebSocketClientMessage.SUBSCRIBE_TITLE_TICKS, listener)
    }

    const onIsLoggedIn = (newValue: boolean) => {
      if (newValue) {
        socket.open()
      } else {
        authStatus.value = WebSocketAuthStatus.UNAUTHENTICATED
        socketStatus.value = WebSocketStatus.CLOSED
        socket.close()
      }
    }

    // trigger login check immediate, to open socket after auth store hydration
    watch(isLoggedIn, onIsLoggedIn, { immediate: true })

    return {
      socketStatus,
      authStatus,
      isSocketReady,
      lastData,
      emitSubscribeStdSignals,
      emitSubscribeSignals,
      emitSubscribeWatchlist,
      emitSubscribeList,
      emitSubscribeTitleMessages,
      emitSubscribeTitlesMessages,
      emitSubscribeTitles,
      emitUnsubscribeTitles,
      emitSubscribeTitleTicks,
      emitSubscribeTitlesTicks
    }
  })

/*
TODO: check what else needs to be implemented
Dashboard known events:
Socket.on('new_stats', function(data) {
Socket.on('fingerprint', function(data) {
Socket.on('complete order', function(data) {
Socket.on('strategy_signal', function(data) {
Socket.on('backtest error', function(resp) {
Socket.on('backtest result', function(resp) {
Socket.on('report', function(data) {
Socket.on('message', function(data) {
Socket.on('std_signal', function(data) {
Socket.on('tick', function(data) {
Socket.on('buzz', function(data) {
Socket.on('title status', function(data) {
Socket.on('signal', function(data) {
 */
